import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Layers,
  List,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Square,
} from "lucide-react";
import { useState } from "react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { usePageTitle } from "@/lib/use-page-title";
import type {
  IntegrationHealthEvent,
  IntegrationHealthGroup,
  IntegrationHealthListResponse,
} from "@tilt/admin-sdk";

/** Period preset → ``since`` ISO. ``null`` lets the operator pick a
 *  custom range from the date inputs that appear when "Custom" is
 *  active. The default is "last hour" - matches the backend's default
 *  ``since`` so the first render renders the same window the API
 *  would use anyway. Tightened from 24h to 1h on operator request:
 *  triage-on-call almost always starts with "what's broken right now". */
type PeriodPreset = "1h" | "24h" | "7d" | "30d" | "custom";

const PERIOD_PRESETS: ReadonlyArray<{ readonly key: PeriodPreset; readonly label: string }> = [
  { key: "1h", label: "Last hour" },
  { key: "24h", label: "Last 24h" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "custom", label: "Custom" },
];

const PAGE_SIZE = 100;

function presetToSince(preset: PeriodPreset): string | null {
  const now = new Date();
  if (preset === "1h") return new Date(now.getTime() - 60 * 60_000).toISOString();
  if (preset === "24h") return new Date(now.getTime() - 24 * 60 * 60_000).toISOString();
  if (preset === "7d") return new Date(now.getTime() - 7 * 24 * 60 * 60_000).toISOString();
  if (preset === "30d") return new Date(now.getTime() - 30 * 24 * 60 * 60_000).toISOString();
  return null;
}

/**
 * /integrations/health - operator + support surface for vendor-side
 * failures (V0072).
 *
 * Visibility (enforced server-side):
 *   * super_admin → every event in the environment.
 *   * partner_admin → events scoped to their partner when
 *     the pos.debug.integration_health feature is enabled for their scope.
 */
export function IntegrationHealthPage(): JSX.Element {
  usePageTitle("Integration health");

  const [includeAck, setIncludeAck] = useState(false);
  const [providerFilter, setProviderFilter] = useState("");
  const [featurePathFilter, setFeaturePathFilter] = useState("");
  const [period, setPeriod] = useState<PeriodPreset>("1h");
  const [customSince, setCustomSince] = useState<string>(""); // YYYY-MM-DD
  const [customUntil, setCustomUntil] = useState<string>(""); // YYYY-MM-DD
  const [grouped, setGrouped] = useState(false);
  const [offset, setOffset] = useState(0);
  // Multi-select acknowledge - IDs the operator has ticked across the
  // current page. Resets on filter / page change so a stale selection
  // never targets rows the operator can no longer see.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const qc = useQueryClient();
  // Bulk acknowledge - single POST to the server-side bulk endpoint.
  // The prior per-id parallel loop hit API Gateway / Lambda
  // concurrency throttling and returned 503s for most rows once the
  // operator selected ≥10. The bulk endpoint flips the whole batch
  // in one transaction.
  const bulkAck = useMutation({
    mutationFn: (ids: ReadonlyArray<string>) =>
      apiClient.bulkAcknowledgeIntegrationHealthEvents(ids),
    onSuccess: () => {
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ["integration-health", "events"] });
    },
  });

  const since =
    period === "custom"
      ? customSince
        ? customSince
        : undefined
      : (presetToSince(period) ?? undefined);
  const until = period === "custom" && customUntil ? customUntil : undefined;

  const events = useQuery<IntegrationHealthListResponse>({
    queryKey: [
      "integration-health",
      "events",
      {
        includeAck,
        providerFilter,
        featurePathFilter,
        period,
        customSince,
        customUntil,
        grouped,
        offset,
      },
    ],
    queryFn: () =>
      apiClient.listIntegrationHealthEvents({
        include_acknowledged: includeAck,
        ...(providerFilter ? { provider_slug: providerFilter } : {}),
        ...(featurePathFilter ? { feature_path: featurePathFilter } : {}),
        ...(since ? { since } : {}),
        ...(until ? { until } : {}),
        ...(grouped ? { group: "fingerprint" as const } : {}),
        limit: PAGE_SIZE,
        offset,
      }),
    retry: false,
    placeholderData: (prev) => prev,
  });

  // Reset to first page + clear selection whenever filters / view
  // mode change - without this an active offset stays positioned on a
  // now-mismatched page and the operator sees blank rows after
  // toggling a filter; a stale selection would also target rows the
  // operator can no longer see.
  function resetOffset(): void {
    setOffset(0);
    setSelectedIds(new Set());
  }

  function toggleSelected(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Visible IDs on the current page (events list mode = event.id;
  // grouped mode = group.latest_event_id). Drives the master "Select
  // all" checkbox + "select all" / "clear" toggle.
  const visibleIds: ReadonlyArray<string> = events.data?.grouped
    ? events.data.groups.filter((g) => g.unacked_count > 0).map((g) => g.latest_event_id)
    : (events.data?.events.filter((ev) => !ev.is_acknowledged).map((ev) => ev.id) ?? []);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  function toggleSelectAllVisible(): void {
    if (allVisibleSelected) {
      // Drop only the visible IDs - keeps any selection from a prior
      // page intact (operator may have selected on previous page +
      // paginated forward).
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of visibleIds) next.add(id);
        return next;
      });
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Integration health</h1>
          <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
            Vendor-side failures captured at every PaymentProvider / InventoryProvider call
            boundary. Filter by period / provider / feature, group identical rows by fingerprint,
            acknowledge once triaged. Default = last hour, unacknowledged only.
          </p>
        </div>
        <button
          type="button"
          onClick={() => events.refetch()}
          disabled={events.isFetching}
          className="inline-flex shrink-0 items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
          title="Reload latest events"
        >
          {events.isFetching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Refresh
        </button>
      </div>

      {/* Filter strip - period preset + filters + view toggle. Soft
          card surface so the strip reads as one panel, not three loose
          rows. */}
      <section
        aria-labelledby="ih-filters-heading"
        className="space-y-3 rounded-lg border bg-card p-4 shadow-sm sm:p-5"
      >
        <h2 id="ih-filters-heading" className="sr-only">
          Filters
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {PERIOD_PRESETS.map((p) => {
            const active = period === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  setPeriod(p.key);
                  resetOffset();
                }}
                aria-pressed={active}
                className={
                  active
                    ? "inline-flex items-center rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
                    : "inline-flex items-center rounded-full border bg-background px-3 py-1 text-xs font-medium text-foreground hover:bg-muted"
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {period === "custom" && (
          <div className="flex flex-wrap items-end gap-3">
            <label className="block text-xs font-medium">
              <span className="text-muted-foreground">Since</span>
              <input
                type="date"
                value={customSince}
                onChange={(e) => {
                  setCustomSince(e.target.value);
                  resetOffset();
                }}
                className="mt-1 block rounded-md border bg-background px-2 py-1 text-xs"
              />
            </label>
            <label className="block text-xs font-medium">
              <span className="text-muted-foreground">Until</span>
              <input
                type="date"
                value={customUntil}
                onChange={(e) => {
                  setCustomUntil(e.target.value);
                  resetOffset();
                }}
                className="mt-1 block rounded-md border bg-background px-2 py-1 text-xs"
              />
            </label>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={providerFilter}
            onChange={(e) => {
              setProviderFilter(e.target.value.toLowerCase().trim());
              resetOffset();
            }}
            placeholder="provider slug…"
            className="rounded-md border bg-background px-2 py-1 text-xs"
          />
          <input
            type="text"
            value={featurePathFilter}
            onChange={(e) => {
              setFeaturePathFilter(e.target.value.toLowerCase().trim());
              resetOffset();
            }}
            placeholder="feature_path (e.g. pos.payments.card_present)…"
            className="min-w-[18rem] rounded-md border bg-background px-2 py-1 text-xs"
          />
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={includeAck}
              onChange={(e) => {
                setIncludeAck(e.target.checked);
                resetOffset();
              }}
            />
            Show acknowledged
          </label>

          {/* View toggle - list (one row / event) vs grouped (one row /
              fingerprint). The backend returns a discriminated union;
              the body below picks the right collection field. */}
          <div className="ml-auto inline-flex overflow-hidden rounded-md border">
            <button
              type="button"
              onClick={() => {
                setGrouped(false);
                resetOffset();
              }}
              aria-pressed={!grouped}
              className={
                !grouped
                  ? "inline-flex items-center gap-1 bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
                  : "inline-flex items-center gap-1 bg-background px-3 py-1 text-xs font-medium text-foreground hover:bg-muted"
              }
            >
              <List className="h-3 w-3" aria-hidden="true" />
              Events
            </button>
            <button
              type="button"
              onClick={() => {
                setGrouped(true);
                resetOffset();
              }}
              aria-pressed={grouped}
              className={
                grouped
                  ? "inline-flex items-center gap-1 bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
                  : "inline-flex items-center gap-1 bg-background px-3 py-1 text-xs font-medium text-foreground hover:bg-muted"
              }
            >
              <Layers className="h-3 w-3" aria-hidden="true" />
              Grouped
            </button>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="ih-events-heading"
        className="rounded-lg border bg-card p-4 shadow-sm sm:p-5"
      >
        <h2 id="ih-events-heading" className="sr-only">
          {grouped ? "Grouped events" : "Events"}
        </h2>
        {/* Multi-select toolbar - only renders when there are unacked
            rows in scope. Master checkbox toggles select-all for the
            current page; "Acknowledge N" fires the bulk mutation. */}
        {visibleIds.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b pb-3">
            <button
              type="button"
              onClick={toggleSelectAllVisible}
              className="inline-flex items-center gap-2 text-xs font-medium text-foreground hover:text-primary"
              aria-pressed={allVisibleSelected}
            >
              {allVisibleSelected ? (
                <CheckSquare className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Square className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {allVisibleSelected ? "Deselect all on page" : "Select all on page"}
            </button>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">
                  {selectedIds.size} selected
                </span>
                <button
                  type="button"
                  onClick={() => bulkAck.mutate(Array.from(selectedIds))}
                  disabled={bulkAck.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {bulkAck.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Acknowledge {selectedIds.size}
                </button>
              </div>
            )}
            {bulkAck.isError && !bulkAck.isPending && (
              <span className="text-[11px] text-destructive">
                Some events failed to acknowledge - refresh and retry.
              </span>
            )}
          </div>
        )}
        {someVisibleSelected && !allVisibleSelected && (
          // sr-only hint - lives in the toolbar's aria already, just
          // here so screen readers announce the partial state. (no-op
          // visual; the toolbar above is the visible affordance.)
          <span className="sr-only">
            {selectedIds.size} of {visibleIds.length} selected
          </span>
        )}
        {events.isLoading ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading events…
          </p>
        ) : events.isError ? (
          <ErrorBanner error={events.error} />
        ) : events.data?.grouped ? (
          events.data.groups.length === 0 ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" /> No groups match these filters.
            </p>
          ) : (
            <ul className="divide-y">
              {events.data.groups.map((g) => (
                <GroupRow
                  key={g.latest_event_id}
                  group={g}
                  selected={selectedIds.has(g.latest_event_id)}
                  onToggleSelected={() => toggleSelected(g.latest_event_id)}
                />
              ))}
            </ul>
          )
        ) : !events.data || events.data.events.length === 0 ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" /> No events match these filters -
            every integration is healthy or acknowledged.
          </p>
        ) : (
          <ul className="divide-y">
            {events.data.events.map((ev) => (
              <EventRow
                key={ev.id}
                event={ev}
                selected={selectedIds.has(ev.id)}
                onToggleSelected={() => toggleSelected(ev.id)}
              />
            ))}
          </ul>
        )}

        {events.data && events.data.total > 0 && (
          <Pager
            offset={events.data.offset}
            limit={events.data.limit}
            total={events.data.total}
            hasMore={events.data.has_more}
            onPrev={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            onNext={() => setOffset(offset + PAGE_SIZE)}
            isFetching={events.isFetching}
          />
        )}
      </section>
    </div>
  );
}

function Pager(props: {
  readonly offset: number;
  readonly limit: number;
  readonly total: number;
  readonly hasMore: boolean;
  readonly isFetching: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
}): JSX.Element {
  const start = props.total === 0 ? 0 : props.offset + 1;
  const end = Math.min(props.offset + props.limit, props.total);
  return (
    <div className="mt-3 flex items-center justify-between border-t pt-3 text-[11px] text-muted-foreground">
      <span className="font-mono">
        {start}–{end} of {props.total}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={props.onPrev}
          disabled={props.offset === 0 || props.isFetching}
          className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 hover:bg-muted disabled:opacity-50"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden="true" />
          Prev
        </button>
        <button
          type="button"
          onClick={props.onNext}
          disabled={!props.hasMore || props.isFetching}
          className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 hover:bg-muted disabled:opacity-50"
        >
          Next
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function GroupRow(props: {
  readonly group: IntegrationHealthGroup;
  readonly selected: boolean;
  readonly onToggleSelected: () => void;
}): JSX.Element {
  const { group } = props;
  const qc = useQueryClient();
  const ack = useMutation({
    mutationFn: () => apiClient.acknowledgeIntegrationHealthEvent(group.latest_event_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["integration-health", "events"] });
    },
  });
  const ackable = group.unacked_count > 0;
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      {ackable && (
        <label className="mt-0.5 flex shrink-0 items-center sm:mt-1">
          <input
            type="checkbox"
            checked={props.selected}
            onChange={props.onToggleSelected}
            className="h-4 w-4 cursor-pointer"
            aria-label="Select this group for bulk acknowledge"
          />
        </label>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {group.provider_slug}
          </span>
          {group.feature_path && (
            <span className="font-mono text-[11px] text-muted-foreground">
              {group.feature_path}
            </span>
          )}
          {group.operation && (
            <span className="font-mono text-[11px] text-muted-foreground">· {group.operation}</span>
          )}
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-mono text-foreground">
            ×{group.occurrence_count}
            {group.unacked_count > 0 && group.unacked_count !== group.occurrence_count && (
              <span className="text-amber-700 dark:text-amber-300">
                ({group.unacked_count} unack)
              </span>
            )}
          </span>
        </div>
        <p className="text-sm font-medium">{group.vendor_message ?? "(no vendor message)"}</p>
        <p className="text-[11px] text-muted-foreground">
          <span className="font-mono">{group.vendor_code ?? "-"}</span>
          {" · "}
          first {formatTime(group.first_seen)} · last {formatTime(group.last_seen)}
          {group.sample_request_id && (
            <>
              {" · "}sample req <span className="font-mono">{group.sample_request_id}</span>
            </>
          )}
        </p>
      </div>
      {group.unacked_count > 0 && (
        <button
          type="button"
          onClick={() => ack.mutate()}
          disabled={ack.isPending}
          className="inline-flex shrink-0 items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
          title="Acknowledge the most recent event in this group"
        >
          {ack.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Ack latest
        </button>
      )}
    </li>
  );
}

function EventRow(props: {
  readonly event: IntegrationHealthEvent;
  readonly selected: boolean;
  readonly onToggleSelected: () => void;
}): JSX.Element {
  const { event } = props;
  const qc = useQueryClient();
  const ack = useMutation({
    mutationFn: () => apiClient.acknowledgeIntegrationHealthEvent(event.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["integration-health", "events"] });
    },
  });
  const occurred = formatTime(event.occurred_at);
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      {!event.is_acknowledged && (
        <label className="mt-0.5 flex shrink-0 items-center sm:mt-1">
          <input
            type="checkbox"
            checked={props.selected}
            onChange={props.onToggleSelected}
            className="h-4 w-4 cursor-pointer"
            aria-label="Select this event for bulk acknowledge"
          />
        </label>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {event.provider_slug}
          </span>
          {event.feature_path && (
            <span className="font-mono text-[11px] text-muted-foreground">
              {event.feature_path}
            </span>
          )}
          {event.operation && (
            <span className="font-mono text-[11px] text-muted-foreground">· {event.operation}</span>
          )}
          {event.is_acknowledged && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">
              <Check className="h-3 w-3" aria-hidden="true" />
              acknowledged
            </span>
          )}
        </div>
        <p className="text-sm font-medium">{event.detail}</p>
        {event.vendor_message && (
          <p className="text-[11px] text-muted-foreground">
            <span className="font-mono">{event.vendor_code ?? "-"}</span>{" "}
            <span>{event.vendor_message}</span>
          </p>
        )}
        <SearchContext event={event} />
        <p className="text-[11px] text-muted-foreground">
          <span className="font-mono">{event.partner_path}</span>
          {" · "}
          {occurred}
          {event.request_id && (
            <>
              {" · "}
              req <span className="font-mono">{event.request_id}</span>
            </>
          )}
        </p>
      </div>
      {!event.is_acknowledged && (
        <button
          type="button"
          onClick={() => ack.mutate()}
          disabled={ack.isPending}
          className="inline-flex shrink-0 items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
        >
          {ack.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Acknowledge
        </button>
      )}
    </li>
  );
}

/** Search-context line - surfaces the IDs an operator needs to find
 *  the underlying transaction. UUIDs are truncated to 8-char prefixes
 *  (sufficient to disambiguate in the dataset; full UUID is on the
 *  ``title`` for copy/paste). Each pill copies its full UUID to the
 *  clipboard on click so a support engineer can paste it into
 *  Transactions / Orders / Locations search without retyping.
 *
 *  Pills only render when their column is non-null on this event,
 *  so an inventory error (which carries no payment_id) doesn't show
 *  an empty "Payment: -" slot. The first pill labels the family
 *  ("Search context →") so the row still reads cleanly when only
 *  partner/org rows arrive. */
/** Pill in the search-context line. ``displayValue`` is what shows
 *  on screen (a name when available, falling back to the 8-char UUID
 *  prefix); ``copyValue`` is what lands on the clipboard (always the
 *  full UUID, since that's what searches across other tools). */
interface ContextPill {
  readonly key: string;
  readonly label: string;
  readonly displayValue: string;
  readonly copyValue: string;
}

function SearchContext(props: { readonly event: IntegrationHealthEvent }): JSX.Element | null {
  const { event } = props;
  const items: ContextPill[] = [];
  if (event.partner_id) {
    items.push({
      key: "partner",
      label: "Partner",
      displayValue: event.partner_name ?? event.partner_id.slice(0, 8),
      copyValue: event.partner_id,
    });
  }
  if (event.organization_id) {
    items.push({
      key: "org",
      label: "Org",
      displayValue: event.organization_name ?? event.organization_id.slice(0, 8),
      copyValue: event.organization_id,
    });
  }
  if (event.location_id) {
    items.push({
      key: "loc",
      label: "Loc",
      displayValue: event.location_name ?? event.location_id.slice(0, 8),
      copyValue: event.location_id,
    });
  }
  if (event.order_id) {
    // Order pill prefers the cashier-typed external_reference (PO #
    // they recognise on a printed receipt) over the system UUID prefix.
    // Copy still hands over the order_id UUID for unambiguous lookup.
    items.push({
      key: "order",
      label: "Order",
      displayValue:
        event.order_external_reference ?? `#${event.order_id.slice(0, 8).toUpperCase()}`,
      copyValue: event.order_id,
    });
  }
  if (event.payment_id) {
    items.push({
      key: "payment",
      label: "Payment",
      displayValue: event.payment_id.slice(0, 8),
      copyValue: event.payment_id,
    });
  }
  if (event.binding_id) {
    items.push({
      key: "binding",
      label: "Binding",
      displayValue: event.binding_id.slice(0, 8),
      copyValue: event.binding_id,
    });
  }
  if (items.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="font-medium">Search context →</span>
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(it.copyValue);
          }}
          title={`${it.label}: ${it.copyValue} - click to copy full UUID`}
          className="inline-flex items-center gap-1 rounded border bg-muted/40 px-1.5 py-0.5 text-[11px] hover:bg-muted"
        >
          <span className="font-medium text-muted-foreground">{it.label}:</span>
          <span className="font-mono">{it.displayValue}</span>
        </button>
      ))}
    </p>
  );
}

function ErrorBanner(props: { error: unknown }): JSX.Element {
  return (
    <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
      {describe(props.error)}
    </div>
  );
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
